import React, { useState, useEffect } from 'react';
import './AboutPage.css'; // Import the CSS file
import Footer from "./footer";

const AboutPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeCard, setActiveCard] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stats = [
    { 
      icon: '👥', 
      label: 'Happy Investors', 
      value: '751+' 
    },
    { 
      icon: '📈', 
      label: 'Successful Trades', 
      value: '50K+' 
    },
    { 
      icon: '🏆', 
      label: 'Years Of Experience', 
      value: '8+' 
    }
  ];

  const services = [
    {
      icon: '📊',
      title: 'Buying & Selling Unlisted Shares',
      description: 'We provide a transparent and efficient marketplace for investors looking to invest in unlisted shares of high-growth potential companies before they go public. Companies like Waaree Energies have demonstrated significant pre-IPO growth, driven by their leadership in renewable energy and strong financial performance, making unlisted shares a valuable investment avenue.',
      gradient: 'service-overlay-blue',
      iconGradient: 'service-icon-blue'
    },
    {
      icon: '🎯',
      title: 'Recovery of Lost Shares',
      description: 'Our expertise extends to assisting investors in reclaiming lost, forgotten, or unclaimed shares, ensuring rightful ownership is restored efficiently and without hassle.',
      gradient: 'service-overlay-purple',
      iconGradient: 'service-icon-purple'
    }
  ];

  const features = [
    { 
      text: 'Transparent marketplace for pre-IPO investments', 
      bullet: 'feature-bullet-blue' 
    },
    { 
      text: 'Expert recovery of lost and unclaimed shares', 
      bullet: 'feature-bullet-purple' 
    },
    { 
      text: 'In-depth market research and analysis', 
      bullet: 'feature-bullet-pink' 
    },
    { 
      text: 'Client-centric approach with regulatory expertise', 
      bullet: 'feature-bullet-cyan' 
    }
  ];

  return (
    <div className="about-page">
      {/* Animated Background */}
      <div className="animated-background">
        <div className="background-orb background-orb-1"></div>
        <div className="background-orb background-orb-2"></div>
        <div className="background-orb background-orb-3"></div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div 
            className="hero-content"
            style={{ transform: `translateY(${scrollY * 0.1}px)` }}
          >
            <div className="hero-badge">
              <span style={{ fontSize: '16px' }}>✨</span>
              <span>Trusted Investment Platform</span>
            </div>
            
            <h1 className="hero-title">Our Mission</h1>
            
            <p className="hero-description">
              To create a trusted platform where investors can access unlisted share 
              opportunities and reclaim their rightful investments through transparent, 
              efficient, and client-focused solutions.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon" style={{ fontSize: '32px' }}>
                  {stat.icon}
                </div>
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="section">
        <div className="section-container">
          <div className="section-header">
            <span style={{ fontSize: '32px' }}>🎯</span>
            <h2 className="section-title">Who We Are</h2>
          </div>
          
          <div className="who-we-are-card">
            <p className="who-we-are-text">
              At <span className="brand-name">Niveshly</span>, we specialize in 
              unlocking investment opportunities in the unlisted shares market and 
              helping investors recover their rightful assets.
            </p>
            
            <div className="features-grid">
              <div className="feature-list">
                {features.slice(0, 2).map((feature, index) => (
                  <div key={index} className="feature-item">
                    <div className={`feature-bullet ${feature.bullet}`}></div>
                    <p className="feature-text">{feature.text}</p>
                  </div>
                ))}
              </div>
              <div className="feature-list">
                {features.slice(2).map((feature, index) => (
                  <div key={index} className="feature-item">
                    <div className={`feature-bullet ${feature.bullet}`}></div>
                    <p className="feature-text">{feature.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section">
        <div className="section-container">
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            What We Do
          </h2>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card"
                onMouseEnter={() => setActiveCard(index)}
                onMouseLeave={() => setActiveCard(null)}
              >
                <div className={`service-overlay ${service.gradient}`}></div>
                
                <div className="service-content">
                  <div className={`service-icon-wrapper ${service.iconGradient}`}>
                    <span className="service-icon" style={{ fontSize: '32px' }}>
                      {service.icon}
                    </span>
                  </div>
                  
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  
                  <div className="service-link">
                    <span>Learn more</span>
                    <span>→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="section">
        <div className="section-container">
          <div className="expertise-card">
            <p className="expertise-text">
              With a team of seasoned professionals, we leverage in-depth market 
              research—including pre-IPO performance analysis and industry trend 
              forecasting—along with regulatory expertise in compliance and share 
              transfer norms to deliver seamless investment solutions.
            </p>
            <p className="expertise-text">
              Our client-centric approach ensures the efficient and effective 
              recovery of lost shares.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section">
        <div className="section-container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-description">
              Join us in unlocking your investment potential
            </p>
            <button className="cta-button">
              <span className="cta-button-content">
                Join as Partner
                <span className="cta-button-icon">→</span>
              </span>
              <div className="cta-button-overlay"></div>
            </button>
          </div>
        </div>
      </section>
       <div>
        <Footer/>
      </div>
    </div>
  );
};

export default AboutPage;